import * as React from 'react';
import { EpiserverProperty } from 'sos-common/js/utils/episerver.property';
import { FormSectionBase, FormSectionBaseState } from '../Types/FormSectionBase';
import { setInnerHtml } from 'sos-common/js/utils/helpers';
import { shortuid } from 'sos-common/js/utils/guid';
import { Fileupload } from 'sos-common/js/components/Fileupload/Fileupload';
import { FileExtendedDetails, FileState } from 'sos-common/js/commonTypes/fileupload.types';
import { FormFieldFile } from 'sos-common/js/commonTypes/formField.types';
import { IAppContext } from '../Types/Intyg.types';
import { validateFiles, FileValidation } from '../../../services/fileupload.clientonly.validation.service';
const DEFAULT_MIMETYPES: string = '.pdf,.txt,.docx,.doc,.dot,.jpeg,.jpg,.jpe,.jif,.jfif,.jfi,.png';
export interface IFileUploaderClientOnlyProps {}
export interface IFileUploaderClientOnlyState extends FormSectionBaseState {
  files: FileExtendedDetails[];
}

export enum LocalFieldName {
  doc_tjanstgoringsintyg = 'doc_tjanstgoringsintyg',
}
const StringFormat = (str: string, ...args: string[]) => str.replace(/{(\d+)}/g, (match, index) => args[index] || '');
const BYTES_IN_MEGABYTE: number = 1_000_000;
/**
 * Includes one file upload component: examensbevis
 */
export default class FileUploaderClientOnly extends FormSectionBase<
  IFileUploaderClientOnlyProps,
  IFileUploaderClientOnlyState
> {
  private _acceptFiles: string;
  constructor(props: IFileUploaderClientOnlyProps, context: IAppContext) {
    super(props);

    this._acceptFiles =
      !Array.isArray(context.acceptedFileTypes) || !context.acceptedFileTypes.length
        ? DEFAULT_MIMETYPES
        : context.acceptedFileTypes.map((x) => '.' + x).join(',');

    this.state = {
      errors: this.emptyErrorsMap.set(LocalFieldName.doc_tjanstgoringsintyg, {
        errorMessage: context.Translate('error_mandatory'),
        showMessage: false,
      }),
      files: [],
    };
  }

  onDeleteFile = (file: FileExtendedDetails, fieldName: string, field: FormFieldFile) => {
    const { updateModelUsingKeyValue } = this.context;
    const fileArray = field.value.filter((x) => x.FileId !== file.FileId);
    const { forceComponentTreeReRender, Translate, maxIndividualFileSizeInBytes, maxTotalSizeInBytes } = this.context;
    const errors = new Map(this.state.errors);

    if (fileArray.length < 1)
      errors.set(LocalFieldName.doc_tjanstgoringsintyg, {
        errorMessage: Translate('error_mandatory'),
        showMessage: true,
      });

    this.setState({ errors, files: fileArray }, () => {
      forceComponentTreeReRender();
      this.context.updateModelUsingKeyValue('bilagor', fileArray);
    });
  };

  validateFiles = (files: FormFieldFile) => {
    const { forceComponentTreeReRender, Translate, maxIndividualFileSizeInBytes, maxTotalSizeInBytes } = this.context;
    const errors = new Map(this.state.errors);

    this.context.isFormTouched = true;

    const fileValidation = validateFiles(files, 10, maxIndividualFileSizeInBytes, true);

    switch (fileValidation) {
      case FileValidation.amountOfFilesExceeded:
        errors.set(LocalFieldName.doc_tjanstgoringsintyg, {
          errorMessage: Translate('error_maxantalfiler', '').replace('{ANTAL_FILER}', '10'),
          showMessage: true,
        });
        break;
      case FileValidation.individualFileSizeExceeded:
        errors.set(LocalFieldName.doc_tjanstgoringsintyg, {
          errorMessage:
            Translate('error_maxstorlek') + Math.floor(maxIndividualFileSizeInBytes / BYTES_IN_MEGABYTE) + ' MB',
          showMessage: true,
        });
        break;
      case FileValidation.isRequiredWithZeroFiles:
        errors.set(LocalFieldName.doc_tjanstgoringsintyg, {
          errorMessage: Translate('error_mandatory'),
          showMessage: true,
        });
        break;
      case FileValidation.valid:
        errors.delete(LocalFieldName.doc_tjanstgoringsintyg);
        break;
    }

    if (fileValidation === FileValidation.valid) {
      let totalSizeForEverything = 0;
      files.value.forEach((fileDetail) => {
        totalSizeForEverything += fileDetail.file.size;
      });

      if (totalSizeForEverything > maxTotalSizeInBytes) {
        errors.set(LocalFieldName.doc_tjanstgoringsintyg, {
          errorMessage: this.context.Translate('error_total_max_file_size'),
          showMessage: true,
        });
      }
    }
    const filesArr = files.value;
    filesArr.forEach((file) => {
      file.status = file.status === FileState.UPLOADING ? FileState.SETTLED : file.status;
      file.FileId = shortuid();
    });

    this.setState({ errors, files: filesArr }, () => {
      forceComponentTreeReRender();
      this.context.updateModelUsingKeyValue('bilagor', filesArr);
    });
  };

  getErrorMessage = (fieldName: string) => {
    return this.state.errors.get(fieldName)?.showMessage || this.context.submitButtonIsClicked
      ? this.state.errors.get(fieldName)?.errorMessage
      : null;
  };
  public render() {
    const { isInEditMode, maxIndividualFileSizeInBytes, Translate } = this.context;
    return (
      <section>
        <EpiserverProperty propertyName="DokumentHeading" isInEditMode={isInEditMode}>
          <h2>{this.findProperty('DokumentHeading')}</h2>
        </EpiserverProperty>

        <EpiserverProperty propertyName="DokumentPreamble" wrappingElement="span" isInEditMode={isInEditMode}>
          <p className="preamble">{this.findProperty('DokumentPreamble')}</p>
        </EpiserverProperty>

        <EpiserverProperty propertyName="DokumentBody" usingDangerouslySetInnerHTML={true} isInEditMode={isInEditMode}>
          <div className="m-t-1" dangerouslySetInnerHTML={setInnerHtml(this.findProperty('DokumentBody'))} />
        </EpiserverProperty>

        <div className="m-t-2">
          <Fileupload
            heading={Translate(LocalFieldName.doc_tjanstgoringsintyg)}
            errorMessage={this.getErrorMessage(LocalFieldName.doc_tjanstgoringsintyg)}
            fieldName={LocalFieldName.doc_tjanstgoringsintyg}
            isRequired={true}
            mandatory={true}
            disabled={false}
            onRemove={this.onDeleteFile}
            onAdd={this.validateFiles}
            clientOnly={true}
            value={this.state.files}
            clientOnlyLegitimation={true}
            max={10}
            maxFileSize={maxIndividualFileSizeInBytes}
            maxFileSizeErrorMessage={Translate('maxstorlek')}
            mimetype={this._acceptFiles}
            infoBubble={{ text: this.findProperty('DokumentInforuta'), isInEditMode, propertyName: 'DokumentInforuta' }}
            upload={Translate('laddaupp')}
            nofilesText={Translate('ingafiler')}
            submitButtonIsClicked={this.context.submitButtonIsClicked}
            acceptedFileTypes={this.context.acceptedFileTypes}
            wrongFileTypeErrorMessage={StringFormat(
              Translate('error_filetype'),
              this._acceptFiles?.replaceAll(',', ', ')
            )}
          />
        </div>
      </section>
    );
  }
}
